/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
// false positive no-unnecessary-type-assertion
// https://typescript-eslint.io/rules/no-unnecessary-condition/#possibly-undefined-indexed-access
import { compact, get, isEmpty, isNil } from 'lodash';
import type {
  Analysis,
  CustomizedBlock,
  FactorExposureComponents,
  PortfolioContributionFactorExposureNode,
  PortfolioContributionPerformanceSummary,
  PortfolioContributionPerformanceSummaryResponse,
  ReturnsFactorAnalysis,
  ReturnsHistogram,
  Scenario,
} from 'venn-api';
import {
  type CategoryMetric,
  type CustomizableMetric,
  FACTOR_PORTFOLIO_CONTRIBUTION_BLOCK_SETTINGS,
  isMetricAlwaysInPercentFormat,
  Numbers,
  PORTFOLIO_CONTRIBUTION_BLOCK_SETTINGS,
  RESIDUAL_FACTOR_ID,
  RISK_FREE_RATE_FACTOR_ID,
  TOTAL_FACTOR_ID,
} from 'venn-utils';
import { convertByUnits, getUnitPrecision, getUnitSymbol } from '../../utils/scenario';
import { getPortfolioBreakdownDetails } from './portfolioBreakdown/getPortfolioBreakdownDetails';

import { getExcelFormattedScenarioShock, getYAxisLabel } from '../components/charts/returns-distribution/utils';
import { FACTOR_CONTRIBUTION_ANALYSIS_TYPES, TOTAL_LABEL, TOTAL_LABEL_CUMULATIVE } from '../customAnalysisContants';
import type {
  ColumnData,
  DataValue,
  FactorRowData,
  MetricRowData,
  PortfolioBreakdownRowData,
  PortfolioRowData,
  ScenarioColumnData,
  ScenarioRowData,
  ValueMapper,
} from '../types';
import type { StudioRequestSubject } from 'venn-state';

export const getRelativeValue = (relativeToTotal?: boolean, totalValue?: number, value?: number) => {
  if (isNil(value) || totalValue === 0 || !relativeToTotal) {
    return value;
  }

  if (isNil(totalValue)) {
    return undefined;
  }

  return Number(value) / Math.abs(totalValue);
};

export const portfolioStrategyBreakdownParser = (
  _selectedBlock: CustomizedBlock,
  analysesGroup?: (Analysis | undefined)[][],
): PortfolioRowData<PortfolioBreakdownRowData>[] => {
  const analysis = analysesGroup?.[0]?.[0];
  const portfolioBreakdownItems = analysis?.portfolioBreakdown?.[0]?.items;

  if (isNil(portfolioBreakdownItems)) {
    return [];
  }

  return portfolioBreakdownItems.map((item) => {
    const details = item.children?.length ? getPortfolioBreakdownDetails(item.children) : undefined;

    return {
      label: item.label,
      key: item.label,
      allocation: [item.allocation],
      isStrategy: isNil(item.fund),
      fundId: item.fund?.id,
      path: item.path,
      value: [
        {
          weight: item.weight,
          hierarchy: details?.hierarchy,
        },
      ],
    };
  });
};

export const scenarioParser =
  (scenarios: Scenario[]) =>
  (
    selectedBlock: CustomizedBlock,
    analysesGroup?: (Analysis | undefined)[][],
  ): ScenarioColumnData[] | ScenarioRowData[] => {
    const firstSuccessfulScenario = analysesGroup?.find((s) => s?.[0]?.scenarios?.length);

    if (selectedBlock.infoGraphicType === 'DISTRIBUTE_BAR') {
      if (!analysesGroup) {
        return [];
      }

      return analysesGroup.flatMap((group) =>
        group[0]
          ? ({
              xAxisCategories:
                firstSuccessfulScenario && firstSuccessfulScenario[0]?.scenarios
                  ? firstSuccessfulScenario[0]?.scenarios
                      .find((s) => !!s)
                      ?.map(
                        (groupScenario, index) =>
                          `${scenarios[index]!.fundName}:\n${scenarios[index]!.shock > 0 ? '+' : ''}${parseFloat(
                            convertByUnits(scenarios[index]!.shock, scenarios[index]!.units),
                          ).toFixed(getUnitPrecision(scenarios[index]!.units))} ${getUnitSymbol(
                            scenarios[index]!.units,
                          )}`,
                      )
                  : [],
              yAxisTitle: `Estimated${selectedBlock.relativeToBenchmark ? ' Relative' : ''} Return`,
              percent: true,
              seriesData: group[0]?.scenarios
                ? group[0]?.scenarios
                    .find((s) => !!s)
                    ?.map((groupScenario) =>
                      !isNil(groupScenario.predicted)
                        ? Number.parseFloat(Numbers.safeFormatNumber(groupScenario.predicted * 100, 2))
                        : '--',
                    )
                : [],
              exportable: true,
              relative: selectedBlock.relativeToBenchmark,
              scenarioAnalyses: group?.length && group[0]?.scenarios?.length ? group[0].scenarios[0] : undefined,
              scenarios,
            } as ScenarioColumnData)
          : [],
      );
    }

    const rows =
      analysesGroup && firstSuccessfulScenario
        ? firstSuccessfulScenario
            .find((s) => s?.scenarios?.length)
            ?.scenarios[0]?.map((scenario, index) => {
              const scenarioAnalyses = analysesGroup.map((group) => {
                return group?.[0]?.scenarios?.[0]?.[index];
              });
              return {
                label: scenarios[index]!.fundName,
                scenarioShockXLS: getExcelFormattedScenarioShock(scenarios[index]!),
                scenarioAnalysis: scenarioAnalyses,
                scenario: scenarios[index],
                relative: selectedBlock.relativeToBenchmark,
                hasError: scenarioAnalyses.some((analysis) => analysis?.status === 'FAILURE'),
              } as ScenarioRowData;
            })
        : [];

    return rows || [];
  };

export const performanceSummaryParser =
  (metrics: CustomizableMetric[]) =>
  (selectedBlock: CustomizedBlock, analysesGroup?: (Analysis | undefined)[][]): MetricRowData[] => {
    const relative = selectedBlock.relativeToBenchmark;
    const rows = selectedBlock.selectedMetrics.map((key) => {
      const metric = metrics.find((m) => m.key === key);
      if (!metric) {
        return undefined;
      }

      const label = relative ? (metric.relativeLabel ?? metric.label) : metric.label;

      // Special case for periodReturn
      const analysis = analysesGroup?.[0]?.find((a) => a?.analysisType === metric.analysisType);
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      const analysisResult = metric.analysisResultKey ? analysis?.[metric.analysisResultKey] : analysis;
      const cumulativeLabel =
        key === 'periodReturn' && analysisResult?.[0]?.periodAnnualized === false ? ' (Cumulative)' : '';

      return {
        key,
        label: `${label}${cumulativeLabel}`,
        value: (analysesGroup?.map((analyses) => {
          const analysis = analyses.find((a) => a?.analysisType === metric.analysisType);
          const path = relative && metric.relativeMetricPath ? metric.relativeMetricPath : metric.metricPath;
          return path
            ? // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
              get(analysis?.[metric.analysisResultKey!]?.[0], path)
            : // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
              analysis?.[metric.analysisResultKey!]?.[0];
        }) ?? []) as DataValue[],
        type: metric.dataType,
      };
    });

    return compact(rows);
  };

type DataMapper = { [key: string]: PortfolioRowData<ValueMapper> };
type InvestmentAttributionRow = {
  name: string;
  value?: number | null;
  contributionValue?: number | null;
};

export const portfolioContributionParser =
  (metrics: CustomizableMetric[], subjects: StudioRequestSubject[]) =>
  (selectedBlock: CustomizedBlock, analysesGroup?: (Analysis | undefined)[][]): PortfolioRowData<ValueMapper>[] => {
    const rows: PortfolioRowData<ValueMapper>[] = [];

    const totalLabel =
      analysesGroup?.[0]?.[0]?.historicalPerformancePortfolioContributions?.[0]?.periodAnnualized === false
        ? TOTAL_LABEL_CUMULATIVE
        : TOTAL_LABEL;
    const totalRow: PortfolioRowData<ValueMapper> = {
      label: totalLabel,
      path: [totalLabel],
      value: [],
      isStrategy: true,
      allocation: [],
    };
    const mapper: DataMapper = {};
    const relative = selectedBlock.relativeToBenchmark;
    const relativeToTotal = selectedBlock.contributionToPercentage;

    prefillDataMapperRows(mapper, analysesGroup);

    analysesGroup?.forEach((analyses, index) => {
      const isHistorical = !!subjects[index]?.portfolio?.historical;
      parsePortfolioContributionRows(
        metrics,
        analyses,
        relative,
        totalRow,
        index,
        relativeToTotal,
        mapper,
        isHistorical,
      );
      parsePortfolioFactorContributionRows(analyses, metrics, relative, totalRow, index, mapper, relativeToTotal);
    });
    rows.push(totalRow);
    rows.push(...Object.values(mapper));
    return rows;
  };

const gatherAllPortfolios = (analysesGroup?: (Analysis | undefined)[][]) => {
  const allAnalysisResultKeys = [
    ...new Set(
      [PORTFOLIO_CONTRIBUTION_BLOCK_SETTINGS, FACTOR_PORTFOLIO_CONTRIBUTION_BLOCK_SETTINGS].flatMap((settings) =>
        settings.metrics.map((metric) => metric.analysisResultKey),
      ),
    ),
  ];

  return analysesGroup?.flatMap((analyses) => {
    return compact(
      analyses.flatMap((analysis) =>
        allAnalysisResultKeys.map((key) => {
          const response = analysis?.[key]?.[0];

          if (!response) {
            return null;
          }

          if ('portfolio' in response) {
            return response.portfolio;
          }

          if ('portfolioContribution' in response) {
            return response.portfolioContribution;
          }

          return null;
        }),
      ),
    );
  });
};

export type Node = string[];
export type Graph = Map<string, Node[]>;
type PortfolioNode =
  | PortfolioContributionFactorExposureNode
  | PortfolioContributionPerformanceSummary
  | PortfolioContributionPerformanceSummaryResponse;

/**
 * Does a DFS traversal and calls the callback for each path
 * This is reused for portfolio contribution and more recent portfolio comparison blocks
 */
export const runDfs = (graph: Graph, topLevelPaths: Node[], callback: (path: Node) => void) => {
  const visited = new Set<string>();

  const dfs = (path: Node) => {
    const key = toKey(path);
    if (visited.has(key)) {
      return;
    }
    visited.add(key);

    callback(path);

    for (const child of graph.get(key) ?? []) {
      dfs(child);
    }
  };

  for (const path of topLevelPaths) {
    dfs(path);
  }
};

const buildGraph = (
  portfolios: PortfolioNode[],
): { graph: Graph; topLevelPaths: Node[]; pathToDataMap: DataMapper } => {
  const pathToDataMap: DataMapper = {};
  const graph: Graph = new Map();
  const topLevelPaths: Node[] = [];

  const generatePaths = (portfolio: PortfolioNode) => {
    const generatePathsImpl = (
      portfolio: PortfolioNode,
      parentPath: string[],
      usedNamesMap: Record<string, number>,
    ) => {
      const currentName = getTreeRowCurrentName(portfolio, usedNamesMap);
      const currentPath = [...parentPath, currentName];

      const key = toKey(currentPath);
      pathToDataMap[key] = {
        label: portfolio.name,
        optionData: portfolio.optionData,
        path: currentPath,
        value: [],
        allocation: [],
        fundId: portfolio?.fundId,
        isStrategy: !portfolio.fundId,
      };

      if (isEmpty(parentPath)) {
        topLevelPaths.push(currentPath);
      } else {
        const parentKey = toKey(parentPath);
        if (!graph.has(parentKey)) {
          graph.set(parentKey, []);
        }
        graph.get(parentKey)?.push(currentPath);
      }

      const childrenUsedNamesMap = {};
      portfolio.children?.map((child) => generatePathsImpl(child, currentPath, childrenUsedNamesMap));
    };

    // We should skip the portfolio itself (root node) and start from top-level strategies
    const childrenUsedNamesMap = {};
    portfolio.children?.forEach((portfolio) => generatePathsImpl(portfolio, [], childrenUsedNamesMap));
  };

  portfolios.forEach((portfolio) => generatePaths(portfolio));

  return {
    graph,
    topLevelPaths,
    pathToDataMap,
  };
};

const prefillDataMapperRows = (mapper: DataMapper, analysesGroup?: (Analysis | undefined)[][]) => {
  const allPortfolios = gatherAllPortfolios(analysesGroup) ?? [];

  const { graph, topLevelPaths, pathToDataMap } = buildGraph(allPortfolios);

  runDfs(graph, topLevelPaths, (path) => {
    const key = toKey(path);
    mapper[key] = pathToDataMap[key]!;
  });
};

export const returnsDistributionParser =
  (metrics: CustomizableMetric[]) =>
  (selectedBlock: CustomizedBlock, analysesGroup?: (Analysis | undefined)[][]): ColumnData[] => {
    const analysis = analysesGroup?.[0]?.[0];
    const metric = metrics?.[0];
    if (isNil(analysis) || isNil(metric)) {
      return [];
    }

    const analysisResults = get(analysis, metric.analysisResultKey!) as ReturnsHistogram[];
    const seriesData = analysisResults.map<(number | null)[]>((returnsHistogram) =>
      (returnsHistogram?.returns || []).map(({ count }) => count),
    );
    const yAxisLabel = getYAxisLabel(analysisResults?.[0]?.frequency ?? 'MONTHLY');
    const xAxisCategories = analysisResults?.[0]?.returns?.map(({ name }: { name: string }) => name);

    return seriesData.map((serieData, index) => ({
      seriesData: serieData,
      xAxisCategories,
      yAxisLabel,
      exportable: analysis.exportable[index]!,
    }));
  };

// This contains null byte so should be safe to use as separator
const SEPARATOR = '#\u0000#';

export const toKey = (path: string[]) => path.join(SEPARATOR);

const getTreeRowCurrentName = (
  node: PortfolioContributionFactorExposureNode | PortfolioNode,
  usedNamesMap: {
    [key: string]: number;
  },
) => {
  const currentNameIdx = usedNamesMap[node.name] ?? 0;
  usedNamesMap[node.name] = currentNameIdx + 1;
  return `${node.name}(${currentNameIdx})`;
};

const fillTreeRowData = (
  summary: PortfolioContributionPerformanceSummary,
  index: number,
  parentPath: string[],
  mapper: DataMapper,
  isRoot: boolean,
  relativeToTotal: boolean,
  totalAllocation: number,
  totalValue: number | undefined,
  key: string,
  valuePath: string | undefined,
  usedNamesMap: { [name: string]: number },
  isHistorical: boolean,
) => {
  const currentName = getTreeRowCurrentName(summary, usedNamesMap);

  if (!isRoot) {
    const currentPath = [...parentPath, currentName];
    const path = currentPath.join(SEPARATOR);
    if (!mapper[path]) {
      mapper[path] = {
        label: summary.name,
        optionData: summary.optionData,
        path: currentPath,
        value: [],
        allocation: [],
        isStrategy: !summary.fundId,
        fundId: summary?.fundId,
      };
    }

    if (key === 'allocation') {
      mapper[path]!.allocation[index] = isHistorical
        ? undefined
        : getRelativeValue(relativeToTotal, totalAllocation, summary.allocation);
    } else if (key === 'allocation-VALUE' || key === 'allocation-PERCENT') {
      const actualRelativeToTotal = isMetricAlwaysInPercentFormat(key);
      const parsedValue = getRelativeValue(actualRelativeToTotal, totalAllocation, summary.allocation);
      mapper[path]!.value[index] = mapper[path]?.value[index] ?? ({} as ValueMapper);
      mapper[path]!.value[index]![key] = isHistorical ? undefined : parsedValue;
    } else {
      const parsedValue = getRelativeValue(
        relativeToTotal,
        totalValue,
        valuePath ? get(summary.value, valuePath) : summary.value,
      );
      mapper[path]!.value[index] = mapper[path]?.value[index] ?? ({} as ValueMapper);
      mapper[path]!.value[index]![key] = parsedValue;
    }
  }

  if (summary?.children && summary.children.length > 0) {
    const childrenNamesMap = {};
    const newParentPath = isRoot ? [] : [...parentPath, currentName];
    summary.children.forEach((p) =>
      fillTreeRowData(
        p,
        index,
        newParentPath,
        mapper,
        false,
        relativeToTotal,
        totalAllocation,
        totalValue,
        key,
        valuePath,
        childrenNamesMap,
        isHistorical,
      ),
    );
  }
};

const fillFactorTreeRowData = (
  summary: PortfolioContributionFactorExposureNode,
  index: number,
  parentPath: string[],
  mapper: DataMapper,
  isRoot: boolean,
  relativeToTotal: boolean,
  totalAllocation: number,
  totalValue: { [key: number]: number },
  key: string,
  factors: number[] | undefined,
  valueField: string | undefined,
  valuePath: string | undefined,
  usedNamesMap: { [key: string]: number },
) => {
  const currentName = getTreeRowCurrentName(summary, usedNamesMap);

  if (!isRoot && factors) {
    for (const factor of factors) {
      const currentPath = [...parentPath, currentName];
      const path = currentPath.join(SEPARATOR);
      if (!mapper[path]) {
        mapper[path] = {
          label: summary.name,
          optionData: summary.optionData,
          path: currentPath,
          value: [],
          allocation: [],
          isStrategy: !summary.fundId,
        };
      }

      if (key === 'allocation') {
        mapper[path]!.allocation[index] = getRelativeValue(relativeToTotal, totalAllocation, summary.allocation);
      } else {
        const parsedValue = getRelativeValue(
          relativeToTotal,
          totalValue[factor] ?? 0.0,
          // @ts-expect-error: TODO fix strictFunctionTypes
          valuePath ? get(summary.value, `${valueField}.${factor}.${valuePath}`) : summary.value,
        );

        mapper[path]!.value[index] = mapper[path]?.value[index] ?? ({} as ValueMapper);
        mapper[path]!.value[index]![`${key}.${factor}`] = parsedValue;
      }
    }
  }

  if (summary?.children && summary.children.length > 0) {
    const newParentPath = isRoot ? [] : [...parentPath, currentName];
    const childrenNamesMap = {};
    summary.children.forEach((p) =>
      fillFactorTreeRowData(
        p,
        index,
        newParentPath,
        mapper,
        false,
        relativeToTotal,
        totalAllocation,
        totalValue,
        key,
        factors,
        valueField,
        valuePath,
        childrenNamesMap,
      ),
    );
  }
};

export const factorsParser =
  (metrics: CustomizableMetric[], factors?: CategoryMetric[]) =>
  (selectedBlock: CustomizedBlock, analysesGroup?: (Analysis | undefined)[][]): FactorRowData[] => {
    const relativeToTotal = selectedBlock.contributionToPercentage;
    const totalValues: ValueMapper[] = (analysesGroup ?? []).map((analyses) => {
      const totalValue = {};
      metrics.forEach((metric) => {
        const analysis = analyses.find((a) => a?.analysisType === metric.analysisType);
        if (analysis) {
          if (analysis.analysisType === 'FACTOR_CONTRIBUTION_TO_RISK') {
            // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
            totalValue[metric.key] = analysis[metric.analysisResultKey!]?.[0]?.annualizedTotalRisk;
          } else if (analysis.analysisType === 'FACTOR_CONTRIBUTION_TO_RETURN') {
            // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
            totalValue[metric.key] = analysis[metric.analysisResultKey!]?.[0]?.periodTotalReturn;
          }
        }
      });
      return totalValue;
    });

    const selectedFactors = compact(
      selectedBlock.selectedFactors?.map((f) => factors?.find((factor) => f === factor.id)),
    );

    return selectedFactors.map((f) => ({
      key: f.id,
      label: f.name,
      value: getFactorValues(relativeToTotal, totalValues, f.id, metrics, analysesGroup),
    }));
  };

const getFactorValues = (
  relativeToTotal: boolean,
  totalValue: ValueMapper[],
  factorId: string,
  metrics: CustomizableMetric[],
  analysesGroup?: (Analysis | undefined)[][],
) => {
  const specialRows = [TOTAL_FACTOR_ID, RISK_FREE_RATE_FACTOR_ID, RESIDUAL_FACTOR_ID];
  const isSpecialRows = specialRows.includes(factorId);

  return (analysesGroup ?? []).map((analyses, index) => {
    const parsedValue = {};
    metrics.forEach((metric) => {
      const analysis = analyses.find((a) => a?.analysisType === metric.analysisType);
      if (analysis) {
        if (isSpecialRows) {
          if (analysis.analysisType === 'FACTOR_CONTRIBUTION_TO_RISK') {
            if (factorId === TOTAL_FACTOR_ID) {
              // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
              parsedValue[metric.key] = analysis.factorContributionToRisk?.[0]?.annualizedTotalRisk;
            } else if (factorId === RESIDUAL_FACTOR_ID) {
              // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
              parsedValue[metric.key] = analysis.factorContributionToRisk?.[0]?.residualMarginalRisk;
            }
          } else if (analysis.analysisType === 'FACTOR_CONTRIBUTION_TO_RETURN') {
            if (factorId === TOTAL_FACTOR_ID) {
              // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
              parsedValue[metric.key] = analysis.factorContributionToReturn?.[0]?.periodTotalReturn;
            } else if (factorId === RESIDUAL_FACTOR_ID) {
              // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
              parsedValue[metric.key] = analysis.factorContributionToReturn?.[0]?.periodResidualReturn;
            } else if (factorId === RISK_FREE_RATE_FACTOR_ID) {
              // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
              parsedValue[metric.key] = analysis.factorContributionToReturn?.[0]?.riskFreeReturn;
            }
          }
        } else {
          const contribution = // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
            (analysis[metric.analysisResultKey!]?.[0] as ReturnsFactorAnalysis | undefined)?.factors.find(
              (factor) => String(factor.id) === factorId,
            );
          // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
          parsedValue[metric.key] = contribution?.marginalContribution;
        }
        // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
        parsedValue[metric.key] = getRelativeValue(
          relativeToTotal,
          getOptionalNumber(totalValue?.[index]?.[metric.key]),
          // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
          parsedValue[metric.key],
        );
      }
    });
    return parsedValue;
  });
};

const parsePortfolioContributionRows = (
  metrics: CustomizableMetric[],
  analyses: (Analysis | undefined)[],
  relative: boolean | undefined,
  totalRow: PortfolioRowData<ValueMapper>,
  index: number,
  relativeToTotal: boolean | undefined,
  mapper: DataMapper,
  isHistorical: boolean,
) => {
  metrics.forEach((metric) => {
    if (
      FACTOR_CONTRIBUTION_ANALYSIS_TYPES.includes(metric.analysisType!) &&
      metric.key.startsWith('OUTRIGHT-RESIDUAL-')
    ) {
      const analysis = analyses.find((a) => a?.analysisType === metric.analysisType);
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      const factorContribution = analysis?.[metric.analysisResultKey!]?.[0] as FactorExposureComponents | undefined;
      const path = relative && metric.relativeMetricPath ? metric.relativeMetricPath : metric.metricPath;

      if (!factorContribution || !path) {
        return;
      }

      fillResidualAttributionValues(metric.key, index, relativeToTotal, totalRow, mapper, factorContribution, path);
      return;
    }

    if (FACTOR_CONTRIBUTION_ANALYSIS_TYPES.includes(metric.analysisType!)) {
      return;
    }

    const analysis = analyses.find((a) => a?.analysisType === metric.analysisType);

    if (metric.analysisType === 'INVESTMENT_ATTRIBUTION' || metric.analysisType === 'OUTRIGHT_METRICS') {
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      const analysisResult = analysis?.[metric.analysisResultKey!]?.[0];
      const path = relative && metric.relativeMetricPath ? metric.relativeMetricPath : metric.metricPath;
      const attributionValues = path
        ? (get(analysisResult, path) as {
            strategies?: InvestmentAttributionRow[];
            funds?: InvestmentAttributionRow[];
          })
        : undefined;

      if (!attributionValues) {
        return;
      }

      fillInvestmentAttributionValues(metric.key, index, relativeToTotal, totalRow, mapper, {
        strategies: attributionValues.strategies,
        funds: attributionValues.funds,
        strategiesForRows: attributionValues.strategies,
        preferFundsForTotal: metric.analysisType === 'OUTRIGHT_METRICS',
      });
      return;
    }

    // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
    const contribution = analysis?.[metric.analysisResultKey!]?.[0]?.portfolioContribution as
      | PortfolioContributionPerformanceSummary
      | undefined;
    if (contribution) {
      const path = relative && metric.relativeMetricPath ? metric.relativeMetricPath : metric.metricPath;
      const totalValue = path ? get(contribution.value, path) : contribution;
      const totalAllocation = contribution.allocation;
      totalRow.allocation[index] = getRelativeValue(relativeToTotal, totalAllocation, totalAllocation);
      const parsedValue =
        metric.key === 'allocation-VALUE' || metric.key === 'allocation-PERCENT'
          ? isHistorical
            ? undefined
            : getRelativeValue(isMetricAlwaysInPercentFormat(metric.key), totalAllocation, totalAllocation)
          : getRelativeValue(relativeToTotal, totalValue, totalValue);
      totalRow.value[index] = totalRow.value[index] ?? ({} as ValueMapper);
      totalRow.value[index]![metric.key] = parsedValue;

      fillTreeRowData(
        contribution,
        index,
        [],
        mapper,
        true,
        !!relativeToTotal,
        totalAllocation,
        totalValue,
        metric.key,
        path,
        {},
        isHistorical,
      );
    }
  });
};

const fillResidualAttributionValues = (
  key: string,
  index: number,
  relativeToTotal: boolean | undefined,
  totalRow: PortfolioRowData<ValueMapper>,
  mapper: DataMapper,
  factorContribution: FactorExposureComponents,
  metricPath: string,
) => {
  const totalValue = get(
    factorContribution.portfolio.value,
    `factorContribution.${RESIDUAL_FACTOR_ID}.${metricPath}`,
  ) as number | undefined;

  totalRow.value[index] = totalRow.value[index] ?? ({} as ValueMapper);
  totalRow.value[index]![key] = getRelativeValue(relativeToTotal, totalValue, totalValue);

  const strategyCountByName: Record<string, number> = {};
  const fundCountByName: Record<string, number> = {};

  Object.values(mapper).forEach((row) => {
    const countMap = row.isStrategy ? strategyCountByName : fundCountByName;
    const matchIndex = countMap[row.label] ?? 0;

    const match = row.isStrategy
      ? factorContribution.strategies.filter((attributionRow) => attributionRow.strategyName === row.label)[matchIndex]
      : factorContribution.funds.filter((attributionRow) => attributionRow.fundName === row.label)[matchIndex];

    if (!match) {
      return;
    }

    countMap[row.label] = matchIndex + 1;
    const residualFactorContribution = match.factorContribution?.[RESIDUAL_FACTOR_ID];
    const value = residualFactorContribution
      ? (get(residualFactorContribution, metricPath) as number | undefined)
      : undefined;
    row.value[index] = row.value[index] ?? ({} as ValueMapper);
    row.value[index]![key] = getRelativeValue(relativeToTotal, totalValue, value);
  });
};

const fillInvestmentAttributionValues = (
  key: string,
  index: number,
  relativeToTotal: boolean | undefined,
  totalRow: PortfolioRowData<ValueMapper>,
  mapper: DataMapper,
  attributionValues: {
    strategies?: InvestmentAttributionRow[];
    funds?: InvestmentAttributionRow[];
    strategiesForRows?: InvestmentAttributionRow[];
    preferFundsForTotal?: boolean | undefined;
  },
) => {
  const strategies = attributionValues.strategies ?? [];
  const funds = attributionValues.funds ?? [];
  const strategiesForRows = attributionValues.strategiesForRows ?? strategies;
  // OUTRIGHT_METRICS strategies[] span every nesting depth (parent includes children), so summing
  // them double-counts; leaf funds[] are the non-overlapping partition that reconstructs the total.
  const leafSource = funds.length ? funds : strategies;
  const strategySource = strategies.length ? strategies : funds;
  const totalSource = attributionValues.preferFundsForTotal ? leafSource : strategySource;
  const totalValue = totalSource.reduce(
    (sum, row) => sum + (typeof row.contributionValue === 'number' ? row.contributionValue : 0),
    0,
  );

  totalRow.value[index] = totalRow.value[index] ?? ({} as ValueMapper);
  totalRow.value[index]![key] = getRelativeValue(relativeToTotal, totalValue, totalValue);

  const strategyCountByName: Record<string, number> = {};
  const fundCountByName: Record<string, number> = {};

  Object.values(mapper).forEach((row) => {
    const countMap = row.isStrategy ? strategyCountByName : fundCountByName;
    const rows = row.isStrategy ? strategiesForRows : funds;
    const matchIndex = countMap[row.label] ?? 0;
    const matchingRows = rows.filter((attributionRow) => attributionRow.name === row.label);
    const match = matchingRows[matchIndex];

    if (!match) {
      return;
    }

    countMap[row.label] = matchIndex + 1;
    const value = typeof match.value === 'number' ? match.value : undefined;
    row.value[index] = row.value[index] ?? ({} as ValueMapper);
    row.value[index]![key] = getRelativeValue(relativeToTotal, totalValue, value);
  });
};

const parsePortfolioFactorContributionRows = (
  analyses: (Analysis | undefined)[],
  metrics: CustomizableMetric[],
  relative: boolean | undefined,
  totalRow: PortfolioRowData<ValueMapper>,
  index: number,
  mapper: DataMapper,
  relativeToTotal: boolean | undefined,
) => {
  metrics.forEach((metric) => {
    if (
      !FACTOR_CONTRIBUTION_ANALYSIS_TYPES.includes(metric.analysisType!) ||
      metric.key.startsWith('OUTRIGHT-RESIDUAL-')
    ) {
      return;
    }

    const analysis = analyses.find((a) => a?.analysisType === metric.analysisType);
    // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
    const contribution = analysis?.[metric.analysisResultKey!]?.[0] as FactorExposureComponents | undefined;
    const key = metric.key;
    if (contribution) {
      const factors = contribution.factors.map((factor) => factor.id);
      const field = 'factorContribution';
      const path = relative && metric.relativeMetricPath ? metric.relativeMetricPath : metric.metricPath;
      const totalValue: { [key: number]: number } = {};
      const totalAllocation = contribution.portfolio.allocation;
      totalRow.allocation[index] = getRelativeValue(relativeToTotal, totalAllocation, totalAllocation);
      for (const factor of factors) {
        const valuePath = `${field}.${factor}.${path}`;
        totalValue[factor] = valuePath ? get(contribution.portfolio.value, valuePath) : contribution.portfolio.value;
        totalRow.allocation[index] = getRelativeValue(relativeToTotal, totalAllocation, totalAllocation);
        const parsedValue = getRelativeValue(relativeToTotal, totalValue[factor], totalValue[factor]);
        totalRow.value[index] = totalRow.value[index] ?? ({} as ValueMapper);
        totalRow.value[index]![`${key}.${factor}`] = parsedValue;
      }

      fillFactorTreeRowData(
        contribution.portfolio,
        index,
        [],
        mapper,
        true,
        !!relativeToTotal,
        totalAllocation,
        totalValue,
        key,
        factors,
        field,
        path,
        {},
      );
    }
  });
};

function getOptionalNumber(value: string | number | undefined | null) {
  if (isNil(value)) {
    return undefined;
  }

  const numericValue = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isNaN(numericValue) ? undefined : numericValue;
}
